import { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, GripVertical, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/axios';
import type { ApiResponse } from '@maios/shared';

interface ChecklistItem {
  label: string;
  required: boolean;
  order: number;
}

interface DesignSettings {
  handoffChecklistTemplate: ChecklistItem[];
  reviewApprovalRule: string;
  reviewRequiredRoles: string[] | null;
  notifyOnVersionSave: boolean;
  notifyOnPinChange: boolean;
  notifyOnHandoffChange: boolean;
}

interface Props {
  projectId: string;
  isProjectManager: boolean;
}

const APPROVAL_RULES = [
  { value: 'ALL_APPROVE', label: '모든 리뷰어 승인 필요', desc: '모든 리뷰어가 승인해야 디자인이 승인됩니다.' },
  { value: 'MAJORITY', label: '과반수 승인', desc: '과반수 이상의 리뷰어가 승인하면 디자인이 승인됩니다.' },
  { value: 'ANY_APPROVE', label: '1명 이상 승인', desc: '1명 이상의 리뷰어가 승인하면 디자인이 승인됩니다.' },
  { value: 'SPECIFIC_ROLES', label: '특정 역할 필수 승인', desc: '지정된 역할의 리뷰어가 승인해야 합니다.' },
];

const ROLES = ['PROJECT_MANAGER', 'PROJECT_LEADER', 'MEMBER', 'STAKEHOLDER'];
const ROLE_LABELS: Record<string, string> = {
  PROJECT_MANAGER: 'PM',
  PROJECT_LEADER: '리더',
  MEMBER: '멤버',
  STAKEHOLDER: '이해관계자',
};

export default function DesignSettingsTab({ projectId, isProjectManager }: Props) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['designSettings', projectId],
    queryFn: async () => {
      const res = await api.get<ApiResponse<DesignSettings>>(`/projects/${projectId}/design-settings`);
      return res.data.data!;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (settings: Partial<DesignSettings>) => {
      const res = await api.put<ApiResponse<DesignSettings>>(`/projects/${projectId}/design-settings`, settings);
      return res.data.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['designSettings', projectId] });
      toast.success('설정이 저장되었습니다.');
    },
    onError: () => toast.error('설정 저장에 실패했습니다.'),
  });

  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [approvalRule, setApprovalRule] = useState('ALL_APPROVE');
  const [requiredRoles, setRequiredRoles] = useState<string[]>([]);
  const [notifs, setNotifs] = useState({ versionSave: true, pinChange: true, handoffChange: true });
  const initialized = useRef(false);

  useEffect(() => {
    if (data && !initialized.current) {
      setChecklist(data.handoffChecklistTemplate || []);
      setApprovalRule(data.reviewApprovalRule);
      setRequiredRoles(data.reviewRequiredRoles || []);
      setNotifs({
        versionSave: data.notifyOnVersionSave,
        pinChange: data.notifyOnPinChange,
        handoffChange: data.notifyOnHandoffChange,
      });
      initialized.current = true;
    }
  }, [data]);

  const handleSave = () => {
    saveMutation.mutate({
      handoffChecklistTemplate: checklist.map((c, i) => ({ ...c, order: i })),
      reviewApprovalRule: approvalRule as DesignSettings['reviewApprovalRule'],
      reviewRequiredRoles: approvalRule === 'SPECIFIC_ROLES' ? requiredRoles : null,
      notifyOnVersionSave: notifs.versionSave,
      notifyOnPinChange: notifs.pinChange,
      notifyOnHandoffChange: notifs.handoffChange,
    });
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="size-5 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header with save button */}
      {isProjectManager && (
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">디자인 설정</h2>
          <button
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            {saveMutation.isPending && <Loader2 className="size-4 animate-spin" />}
            {saveMutation.isPending ? '저장 중...' : '설정 저장'}
          </button>
        </div>
      )}

      {/* Handoff Checklist Template */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">핸드오프 체크리스트 템플릿</h3>
        <p className="text-sm text-gray-500 mb-4">디자인 핸드오프 시 확인할 항목을 정의합니다.</p>

        <div className="space-y-2">
          {checklist.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <GripVertical size={14} className="text-gray-300 cursor-grab shrink-0" />
              <input
                value={item.label}
                onChange={(e) => {
                  const next = [...checklist];
                  next[idx] = { ...next[idx], label: e.target.value };
                  setChecklist(next);
                }}
                disabled={!isProjectManager}
                className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                placeholder="항목 이름"
              />
              <label className="flex items-center gap-1 text-sm text-gray-500 shrink-0">
                <input
                  type="checkbox"
                  checked={item.required}
                  onChange={(e) => {
                    const next = [...checklist];
                    next[idx] = { ...next[idx], required: e.target.checked };
                    setChecklist(next);
                  }}
                  disabled={!isProjectManager}
                  className="rounded border-gray-300 text-primary-600"
                />
                필수
              </label>
              {isProjectManager && (
                <button onClick={() => setChecklist(checklist.filter((_, i) => i !== idx))}
                  className="p-1 text-gray-400 hover:text-red-500">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>

        {isProjectManager && (
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => setChecklist([...checklist, { label: '', required: false, order: checklist.length }])}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-primary-600 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100"
            >
              <Plus size={12} /> 항목 추가
            </button>
          </div>
        )}
      </div>

      {/* Review Approval Rule */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">리뷰 승인 규칙</h3>
        <p className="text-sm text-gray-500 mb-4">디자인 리뷰의 승인 조건을 설정합니다.</p>

        <div className="space-y-2">
          {APPROVAL_RULES.map((rule) => (
            <label key={rule.value} className="flex items-start gap-2.5 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input
                type="radio"
                name="approvalRule"
                value={rule.value}
                checked={approvalRule === rule.value}
                onChange={(e) => setApprovalRule(e.target.value)}
                disabled={!isProjectManager}
                className="mt-0.5 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <span className="text-sm text-gray-900">{rule.label}</span>
                <p className="text-sm text-gray-500">{rule.desc}</p>
              </div>
            </label>
          ))}
        </div>

        {approvalRule === 'SPECIFIC_ROLES' && (
          <div className="mt-3 ml-6 flex flex-wrap gap-2">
            {ROLES.map((role) => (
              <label key={role} className="flex items-center gap-1.5 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={requiredRoles.includes(role)}
                  onChange={(e) => {
                    setRequiredRoles(e.target.checked
                      ? [...requiredRoles, role]
                      : requiredRoles.filter((r) => r !== role));
                  }}
                  disabled={!isProjectManager}
                  className="rounded border-gray-300 text-primary-600"
                />
                {ROLE_LABELS[role] || role}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Notification Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">변경 알림 필터</h3>
        <p className="text-sm text-gray-500 mb-4">Figma 변경 시 알림 조건을 설정합니다.</p>

        <div className="space-y-3">
          {[
            { key: 'versionSave' as const, label: '새 버전 저장 시 알림', desc: 'Figma에서 새 버전이 저장될 때 담당자에게 알림을 보냅니다.' },
            { key: 'pinChange' as const, label: '확정 버전 이후 변경 시 알림', desc: '고정된 버전 이후에 Figma 파일이 변경되면 개발자에게 알림을 보냅니다.' },
            { key: 'handoffChange' as const, label: '핸드오프 후 변경 시 알림', desc: '핸드오프된 디자인이 변경되면 개발자와 PM에게 알림을 보냅니다.' },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between">
              <div>
                <span className="text-sm text-gray-700">{item.label}</span>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </div>
              <button
                onClick={() => setNotifs({ ...notifs, [item.key]: !notifs[item.key] })}
                disabled={!isProjectManager}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  notifs[item.key] ? 'bg-primary-600' : 'bg-gray-200'
                } ${!isProjectManager ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  notifs[item.key] ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
